/**
 * WidgetSettingsPage
 * 
 * Allows users to configure and manage their home screen widgets.
 * Provides instructions for adding widgets on iOS and Android.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  LayoutGrid,
  Calendar,
  Pill,
  Phone,
  RefreshCw,
  Smartphone,
  Info,
  CheckCircle2,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import widgetDataService, {
  isWidgetSupported,
  getWidgetInstructions,
  syncAllWidgetData,
  requestWidgetInstall,
  WIDGET_TYPES,
} from '@/services/widgetDataService';
import API from '@/utils/api';

const WidgetSettingsPage = () => {
  const navigate = useNavigate();
  const [widgetSupport, setWidgetSupport] = useState(null);
  const [instructions, setInstructions] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [widgetSettings, setWidgetSettings] = useState({
    appointments: true,
    medications: true,
    quickAccess: true,
  });

  useEffect(() => {
    const checkSupport = async () => {
      const support = await isWidgetSupported();
      setWidgetSupport(support);
      setInstructions(getWidgetInstructions());
    };
    checkSupport();
    
    // Load last sync time
    const stored = localStorage.getItem('widget_last_sync');
    if (stored) {
      setLastSync(new Date(stored));
    }
  }, []);

  // Sync widget data
  const handleSync = async () => {
    setSyncing(true);
    
    try {
      // Fetch all data
      const [appointmentsRes, medicationsRes, profileRes] = await Promise.all([
        API.get('/medical/appointment-preps').catch(() => ({ data: [] })),
        API.get('/medical/medications').catch(() => ({ data: [] })),
        API.get('/shared-medical/summary').catch(() => ({ data: {} })),
      ]);
      
      // Sync to widgets
      const results = await syncAllWidgetData({
        appointments: appointmentsRes.data,
        medications: medicationsRes.data,
        quickAccess: {
          emergencyContacts: profileRes.data?.emergency_contacts || [],
          bloodType: profileRes.data?.profile?.blood_type || '',
          allergies: profileRes.data?.allergies || [],
          conditions: profileRes.data?.conditions || [],
        },
      });
      
      const now = new Date();
      setLastSync(now);
      localStorage.setItem('widget_last_sync', now.toISOString());
      
      toast.success('Widgets updated!', {
        description: 'Your home screen widgets now show the latest data',
      });
    } catch (error) {
      console.error('Widget sync error:', error);
      toast.error('Could not update widgets');
    } finally {
      setSyncing(false);
    }
  };

  // Request widget install (Android)
  const handleInstallWidget = async (type) => {
    const result = await requestWidgetInstall(type);
    if (!result.success) {
      toast.info(result.error || 'Please add widgets manually');
    }
  };

  const platform = Capacitor.getPlatform();
  const isNative = Capacitor.isNativePlatform();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="h-9 w-9"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-slate-900">
                Home Screen Widgets
              </h1>
              <p className="text-xs text-slate-500">
                Quick access from your home screen
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4 pb-24">
        {/* Platform Status */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isNative ? 'bg-green-100' : 'bg-amber-100'
              }`}>
                <Smartphone className={`h-5 w-5 ${
                  isNative ? 'text-green-600' : 'text-amber-600'
                }`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900">
                    {isNative ? (
                      platform === 'ios' ? 'iPhone' : 'Android'
                    ) : 'Web Browser'}
                  </span>
                  <Badge variant={isNative ? 'default' : 'secondary'}>
                    {isNative ? 'Widgets Available' : 'Mobile App Required'}
                  </Badge>
                </div>
                <p className="text-sm text-slate-500">
                  {isNative 
                    ? 'Add widgets to your home screen for quick access'
                    : 'Download the mobile app to use home screen widgets'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Available Widgets */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <LayoutGrid className="h-5 w-5 text-blue-500" />
              Available Widgets
            </CardTitle>
            <CardDescription>
              Choose what to show on your home screen
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Appointments Widget */}
            <div className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">Upcoming Appointments</h4>
                  <p className="text-sm text-slate-500">
                    See your next appointments at a glance
                  </p>
                </div>
              </div>
              <Switch
                checked={widgetSettings.appointments}
                onCheckedChange={(v) => setWidgetSettings({...widgetSettings, appointments: v})}
              />
            </div>

            {/* Medications Widget */}
            <div className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Pill className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">Today&apos;s Medications</h4>
                  <p className="text-sm text-slate-500">
                    Track your daily medication schedule
                  </p>
                </div>
              </div>
              <Switch
                checked={widgetSettings.medications}
                onCheckedChange={(v) => setWidgetSettings({...widgetSettings, medications: v})}
              />
            </div>

            {/* Quick Access Widget */}
            <div className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">Quick Access</h4>
                  <p className="text-sm text-slate-500">
                    Emergency contacts, allergies, and vitals
                  </p>
                </div>
              </div>
              <Switch
                checked={widgetSettings.quickAccess}
                onCheckedChange={(v) => setWidgetSettings({...widgetSettings, quickAccess: v})}
              />
            </div>
          </CardContent>
        </Card>

        {/* Sync Data */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-slate-900">Update Widget Data</h4>
                <p className="text-sm text-slate-500">
                  {lastSync 
                    ? `Last updated: ${lastSync.toLocaleTimeString()}`
                    : 'Sync your data to widgets'
                  }
                </p>
              </div>
              <Button
                onClick={handleSync}
                disabled={syncing}
                className="gap-2"
              >
                {syncing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {syncing ? 'Syncing...' : 'Sync Now'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        {instructions && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="h-5 w-5 text-slate-500" />
                {instructions.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-2">
                {instructions.steps.map((step, index) => (
                  <li key={index} className="flex items-start gap-3 text-sm">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-medium">
                      {index + 1}
                    </span>
                    <span className="text-slate-700 pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        )}

        {/* Note about widgets */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Widgets update automatically</p>
              <p className="text-blue-700 mt-1">
                Your widgets will refresh periodically to show the latest information. 
                You can also tap &quot;Sync Now&quot; to update them immediately.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WidgetSettingsPage;
